import { uploadsService } from '@/shared/api/services/uploads.service';
import { createLogger } from '@/shared/lib/logger';
import type { AngleValidation } from '@/types/concept';

const logger = createLogger('AngleValidationPolling');

// Single source of truth for angle-validation polling cadence — unified with
// the mobile platforms. Consumed by useValidationPolling (multi-item upload
// modal) and pollAngleValidation (single-angle flows).
export const VALIDATION_POLL_INTERVAL_MS = 3000;
export const VALIDATION_POLL_INITIAL_DELAY_MS = 10_000;
// One budget for the whole wait, counted from the first poll tick. Queue time
// used to be free, so an intake the queue never picked up was polled forever.
export const VALIDATION_WAIT_TIMEOUT_MS = 600_000;

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll one intake until the server settles it (docs/upload/ADR-002). Fallback
 * for flows where the `uploads/completed` event may be missed — callers abort
 * the signal when the socket path resolves first.
 *
 * Semantics mirror useValidationPolling: 10s initial delay, 3s interval, 600s
 * budget counted from the first poll tick, queue time included. Transient fetch
 * errors are logged and retried.
 *
 * @returns the finished validation plus the view it created (null when the
 *          outcome created no view), or null when the wait exceeded the
 *          timeout budget.
 * @throws DOMException AbortError when the signal aborts.
 */
export async function pollUploadIntake(
  projectId: string,
  uploadId: string,
  signal: AbortSignal
): Promise<{ validation: AngleValidation; angleId: string | null } | null> {
  await sleep(VALIDATION_POLL_INITIAL_DELAY_MS, signal);

  let waitedMs = 0;

  for (;;) {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const upload = await uploadsService.get(projectId, uploadId);
      const validation = upload.attributes?.validation;

      // The server says whether to ask again — never re-derive that from the
      // status, the set of terminal ones keeps growing.
      if (validation && validation.is_in_progress === false) {
        const outcome = upload.attributes.outcome;
        return {
          validation: validation as unknown as AngleValidation,
          angleId: outcome?.type === 'angle' ? outcome.id : null,
        };
      }

      waitedMs += VALIDATION_POLL_INTERVAL_MS;
      if (waitedMs >= VALIDATION_WAIT_TIMEOUT_MS) {
        return null;
      }
    } catch (error) {
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      logger.warn(`Polling error for upload ${uploadId}:`, error);
    }

    await sleep(VALIDATION_POLL_INTERVAL_MS, signal);
  }
}
